// src/pages/Schedule.js
import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { format } from 'date-fns';
import { fr as dfnsFr, enUS as dfnsEn, nl as dfnsNl } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import './Schedule.css';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import { pickL } from '../utils/i18nContent';
import {
  courseMatchesDanceStyle,
  fetchDanceStyles,
  getDanceStyleLabel,
  normalizeDanceStyleIds,
} from '../services/danceStylesService';

function toLocalDateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Fonction pour extraire le prénom seulement
const getFirstName = (teacherString) => {
  if (!teacherString) return '';
  if (teacherString.includes(',')) {
    return teacherString.split(',').map(t => t.trim().split(' ')[0]).join(', ');
  }
  return teacherString.split(' ')[0];
};

// Emoji: on se base sur la valeur FR stockée en base (CourseForm)
const getDifficultyEmoji = (level) => {
  switch(level) {
    case 'Débutant': return '🟢';
    case 'Intermédiaire': return '🟡';
    case 'Avancé': return '🔴';
    default: return '⚪';
  }
};
const getAudienceEmoji = (audience) => {
  switch(audience) {
    case 'Enfants': return '👶';
    case 'Ados': return '🧒';
    case 'Adultes': return '👩';
    case 'Seniors': return '👵';
    default: return '👥';
  }
};

// Ordre (on garde les clés FR stockées dans Firestore)
const DAY_ORDER = { 'Lundi':1,'Mardi':2,'Mercredi':3,'Jeudi':4,'Vendredi':5,'Samedi':6,'Dimanche':7 };

// Traduction des jours d'entête hebdo à l'affichage (les données restent FR comme clés)
const DAY_LABELS = {
  fr: { 'Lundi':'Lundi','Mardi':'Mardi','Mercredi':'Mercredi','Jeudi':'Jeudi','Vendredi':'Vendredi','Samedi':'Samedi','Dimanche':'Dimanche' },
  en: { 'Lundi':'Monday','Mardi':'Tuesday','Mercredi':'Wednesday','Jeudi':'Thursday','Vendredi':'Friday','Samedi':'Saturday','Dimanche':'Sunday' },
  nl: { 'Lundi':'Maandag','Mardi':'Dinsdag','Mercredi':'Woensdag','Jeudi':'Donderdag','Vendredi':'Vrijdag','Samedi':'Zaterdag','Dimanche':'Zondag' }
};

// Mappings FR -> clés i18n (les valeurs DB sont FR)
const DIFF_KEY = { 'Tous niveaux':'allLevels','Débutant':'beginner','Intermédiaire':'intermediate','Avancé':'advanced' };
const AUD_KEY  = { 'Tout public':'all','Enfants':'kids','Ados':'teens','Adultes':'adults','Seniors':'seniors' };

const FILTER_LABELS = {
  fr: { course: 'Cours', style: 'Style de danse', audience: 'Public', level: 'Niveau', allCourses: 'Tous les cours', allStyles: 'Tous les styles', allAudiences: 'Tous les publics', allLevels: 'Tous les niveaux', reset: 'Réinitialiser les filtres', noResults: 'Aucun cours ne correspond aux filtres sélectionnés.' },
  en: { course: 'Course', style: 'Dance style', audience: 'Audience', level: 'Level', allCourses: 'All courses', allStyles: 'All styles', allAudiences: 'All audiences', allLevels: 'All levels', reset: 'Reset filters', noResults: 'No course matches the selected filters.' },
  nl: { course: 'Cursus', style: 'Dansstijl', audience: 'Doelgroep', level: 'Niveau', allCourses: 'Alle cursussen', allStyles: 'Alle stijlen', allAudiences: 'Alle doelgroepen', allLevels: 'Alle niveaus', reset: 'Filters wissen', noResults: 'Geen cursus komt overeen met de geselecteerde filters.' },
};


const Schedule = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('schedule');
  const lang = (i18n.language || 'fr').slice(0,2);
  const dfnsLocale = (lang === 'en') ? dfnsEn : (lang === 'nl' ? dfnsNl : dfnsFr);

  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [danceStyleOptions, setDanceStyleOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedDanceStyle, setSelectedDanceStyle] = useState('');
  const [selectedAudience, setSelectedAudience] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');


  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDateSessions, setSelectedDateSessions] = useState([]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        // Les cours doivent toujours se charger, même si la collection
        // danceStyles est vide, inaccessible ou temporairement indisponible.
        const querySnapshot = await getDocs(collection(db, "dance_courses"));

        let styles = [];
        try {
          styles = await fetchDanceStyles();
        } catch (styleError) {
          console.warn(
            "Les styles de danse n'ont pas pu être chargés. Les cours restent affichés.",
            styleError
          );
        }

        setDanceStyleOptions(Array.isArray(styles) ? styles : []);

        const allCourses = [];
        const allSessions = [];

        querySnapshot.forEach((doc) => {
          const course = { id: doc.id, ...doc.data() };

          if (course.dates && course.dates.length > 0) {
            // Labels profs (liste join)
            const teachers = Array.isArray(course.teachers)
              ? course.teachers.map(t => t.pseudo).join(', ')
              : '';

            // Joins bruts FR depuis DB (pour emojis) + clés i18n afiichage
            const difficultyFR = Array.isArray(course.difficultyLevels) ? course.difficultyLevels : [];
            const audienceFR   = Array.isArray(course.audiences) ? course.audiences : [];
            const danceStyles = normalizeDanceStyleIds(course.danceStyles);
            const difficultyKeys = difficultyFR.map(d => DIFF_KEY[d] || d);
            const audienceKeys   = audienceFR.map(a => AUD_KEY[a] || a);

            course.dates.forEach((dateObj, index) => {
              const rawDate = dateObj.date;
              const sessionDate = rawDate
                ? (
                    typeof rawDate.toDate === 'function'
                      ? rawDate.toDate()
                      : new Date(rawDate)
                  )
                : null;
              const day = dateObj.day || (sessionDate 
                ? sessionDate.toLocaleDateString('fr-FR', { weekday: 'long', timeZone: 'Europe/Paris' }) 
                : '');

              const session = {
                id: `${course.id}-${index}`,
                courseId: course.id,
                // on stocke l'original FR + i18n pour affichage dynamique
                title: course.title || '',
                title_i18n: course.title_i18n || null,
                description: course.description || '',
                description_i18n: course.description_i18n || null,

                date: sessionDate && !Number.isNaN(sessionDate.getTime()) ? sessionDate : null,
                dateString:
                  sessionDate && !Number.isNaN(sessionDate.getTime())
                    ? toLocalDateString(sessionDate)
                    : '',
                day,
                startTime: dateObj.startTime,
                endTime: dateObj.endTime,
                teachers,
                firstNameTeachers: getFirstName(teachers),
                // on garde FR pour emojis et on stocke les clés i18n pour libellés
                difficultyLevelsFR: difficultyFR,
                difficultyKeys,
                audienceFR,
                audienceKeys,
                danceStyles,
                imageUrl: course.imageUrl || '',
                location: course.location || { name: '', address: '' },
                isNewCourse: !!course.isNewCourse,
                isFull: !!course.isFull
              };

              allSessions.push(session);
            });

            allCourses.push({
              id: course.id,
              title: course.title || '',
              title_i18n: course.title_i18n || null,
              description: course.description || '',
              description_i18n: course.description_i18n || null,
              teachers,
              difficultyKeys,
              audienceKeys,
              difficultyLevelsFR: difficultyFR,
              audienceFR,
              danceStyles,
              location: course.location || { name: '', address: '' }
            });
          }
        });

        setCourses(allCourses);
        setSessions(allSessions);
      } catch (error) {
        console.error("Erreur lors du chargement des cours: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchClasses();
  }, []);

  const handleTrialBooking = (session) => {
    const state = {
      selectedPlan: 'essai',
      courseId: session.courseId,
      selectedDate: session.date ? session.date.toISOString().split('T')[0] : '',
      courseTitle: pickL(session.title_i18n ?? session.title, i18n.language, 'fr'),
      prefillData: true,
      sessionTime: `${session.startTime} - ${session.endTime}`,
      teachers: session.firstNameTeachers
    };
    navigate('/inscription', { state });
  };

  // Cours marqué complet (CourseForm.js/CourseEdit.js, champ isFull) : plus de
  // réservation directe, on ouvre la liste d'attente sur la page d'inscription.
  const handleJoinWaitlist = (session) => {
    navigate('/inscription', {
      state: {
        joinWaitlist: true,
        courseId: session.courseId,
        courseTitle: pickL(session.title_i18n ?? session.title, i18n.language, 'fr')
      }
    });
  };

  const filterLabels = FILTER_LABELS[lang] || FILTER_LABELS.fr;

  /*
   * Liste complète des styles affichés dans le filtre.
   *
   * Elle contient :
   * 1. tous les styles actifs récupérés depuis Firestore ;
   * 2. les identifiants encore utilisés par les cours, même si un document
   *    danceStyles est temporairement manquant.
   *
   * Contrairement à getDanceStyleGroups(), cette liste ne masque pas les
   * styles principaux sans enfants ni les styles dont le parent est absent.
   */
  const danceStyleFilterOptions = useMemo(() => {
    const optionsById = new Map();

    (Array.isArray(danceStyleOptions) ? danceStyleOptions : []).forEach((style) => {
      if (!style?.id || style.active === false) return;

      optionsById.set(style.id, {
        id: style.id,
        label: style.label || style.id,
        parentId: style.parentId || '',
        order: Number(style.order) || 0,
      });
    });

    sessions.forEach((session) => {
      normalizeDanceStyleIds(session.danceStyles).forEach((styleId) => {
        if (!optionsById.has(styleId)) {
          optionsById.set(styleId, {
            id: styleId,
            label: getDanceStyleLabel(danceStyleOptions, styleId) || styleId,
            parentId: '',
            order: 9999,
          });
        }
      });
    });

    const options = Array.from(optionsById.values());
    const byId = new Map(options.map((style) => [style.id, style]));

    return options
      .map((style) => ({
        ...style,
        parentLabel: style.parentId
          ? byId.get(style.parentId)?.label || ''
          : '',
      }))
      .sort((a, b) => {
        const aParentOrder = a.parentId
          ? byId.get(a.parentId)?.order ?? 9999
          : a.order;
        const bParentOrder = b.parentId
          ? byId.get(b.parentId)?.order ?? 9999
          : b.order;

        if (aParentOrder !== bParentOrder) {
          return aParentOrder - bParentOrder;
        }

        if (a.parentId !== b.parentId) {
          if (!a.parentId) return -1;
          if (!b.parentId) return 1;
          return String(a.parentLabel).localeCompare(
            String(b.parentLabel),
            'fr'
          );
        }

        if (a.order !== b.order) {
          return a.order - b.order;
        }

        return String(a.label).localeCompare(String(b.label), 'fr');
      });
  }, [danceStyleOptions, sessions]);

  const filteredSessions = useMemo(() => sessions.filter(session => {
    const matchesCourse = !selectedCourseId || session.courseId === selectedCourseId;
    const matchesStyle = !selectedDanceStyle || courseMatchesDanceStyle(session.danceStyles, selectedDanceStyle, danceStyleOptions);
    const matchesAudience = !selectedAudience || session.audienceFR.includes(selectedAudience);
    const matchesDifficulty = !selectedDifficulty || session.difficultyLevelsFR.includes(selectedDifficulty);
    return matchesCourse && matchesStyle && matchesAudience && matchesDifficulty;
  }), [sessions, selectedCourseId, selectedDanceStyle, selectedAudience, selectedDifficulty, danceStyleOptions]);

  const resetFilters = () => {
    setSelectedCourseId('');
    setSelectedDanceStyle('');
    setSelectedAudience('');
    setSelectedDifficulty('');
  };

  const weeklyView = useMemo(() => {
    const sessionsByDay = filteredSessions.reduce((acc, session) => {
      if (!acc[session.day]) acc[session.day] = [];
      const exists = acc[session.day].some(s => 
        s.courseId === session.courseId && s.startTime === session.startTime
      );
      if (!exists) acc[session.day].push(session);
      return acc;
    }, {});

    Object.keys(sessionsByDay).forEach(day => {
      sessionsByDay[day].sort((a, b) => a.startTime.localeCompare(b.startTime));
    });

    return sessionsByDay;
  }, [filteredSessions]);

  // Regroupe les sessions d'un même jour qui ont exactement le même horaire de début
  // (ex: 2 cours en parallèle dans 2 salles différentes) pour les afficher sur la même ligne.
  const groupByTimeSlot = (daySessions) => {
    const groups = [];
    daySessions.forEach(session => {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.startTime === session.startTime) {
        lastGroup.sessions.push(session);
      } else {
        groups.push({ startTime: session.startTime, sessions: [session] });
      }
    });
    return groups;
  };

  const Modal = ({ isOpen, onClose, date, sessions }) => {
    if (!isOpen) return null;
    const formattedDate = date ? format(date, 'EEEE d MMMM yyyy', { locale: dfnsLocale }) : '';
    const cap = formattedDate ? formattedDate[0].toUpperCase() + formattedDate.slice(1) : '';

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="modal-title">{t('modal.title', { date: cap })}</h2>
            <button className="close-button" onClick={onClose}>&times;</button>
          </div>

          {sessions.length === 0 ? (
            <p>{t('modal.empty')}</p>
          ) : (
            sessions.map(session => {
              const title = pickL(session.title_i18n ?? session.title, i18n.language, 'fr');
              const desc  = pickL(session.description_i18n ?? session.description, i18n.language, 'fr');
              return (
                <div key={session.id} className="modal-session">
                  <h3 className="modal-session-title">
                    {title}
                    {session.isNewCourse && <span className="new-badge">🆕 NEW</span>}
                    {session.isFull && <span className="full-badge">🔒 {t('course.full', { defaultValue: 'COMPLET' })}</span>}
                  </h3>
                  <div className="modal-session-time">
                    {session.startTime} - {session.endTime}
                  </div>
                  {desc && <p className="modal-session-description">{desc}</p>}

                  <div className="modal-tags">
                    {session.danceStyles.map(styleId => (
                      <span key={styleId} className="modal-tag style-tag">
                        💃 {getDanceStyleLabel(danceStyleOptions, styleId)}
                      </span>
                    ))}
                    {session.difficultyKeys.map(key => (
                      <span key={key} className="modal-tag difficulty-tag">
                        {getDifficultyEmoji(Object.keys(DIFF_KEY).find(k => DIFF_KEY[k] === key) || '')} {t(`difficulties.${key}`)}
                      </span>
                    ))}
                    {session.audienceKeys.map(key => (
                      <span key={key} className="modal-tag audience-tag">
                        {getAudienceEmoji(Object.keys(AUD_KEY).find(k => AUD_KEY[k] === key) || '')} {t(`audiences.${key}`)}
                      </span>
                    ))}
                  </div>

                  <div className="modal-session-details">
                    <div className="modal-detail-row">
                      <div className="modal-detail-item">
                        <span className="detail-icon">👤</span>
                        <span className="detail-label">{t('labels.teacher')}:</span>
                        <span className="detail-value">{session.firstNameTeachers}</span>
                      </div>
                      {session.location && session.location.name && (
                        <div className="modal-detail-item">
                          <span className="detail-icon">📍</span>
                          <span className="detail-label">{t('labels.place')}:</span>
                          <span className="detail-value">{session.location.name}</span>
                        </div>
                      )}
                    </div>
                    {session.location && session.location.address && (
                      <div className="modal-detail-item full-width">
                        <span className="detail-icon">🏢</span>
                        <span className="detail-label">{t('labels.address')}:</span>
                        <span className="detail-value">{session.location.address}</span>
                      </div>
                    )}
                  </div>

                  {session.isFull ? (
                    <button className="trial-button waitlist-button" onClick={() => handleJoinWaitlist(session)}>
                      {t('buttons.waitlist', { defaultValue: "Rejoindre la liste d'attente" })}
                    </button>
                  ) : (
                    <button className="trial-button" onClick={() => handleTrialBooking(session)}>
                      {t('buttons.trial')}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const openModal = (date, sessions) => {
    setSelectedDate(date);
    setSelectedDateSessions(sessions);
    setIsModalOpen(true);
  };
  const closeModal = () => setIsModalOpen(false);

  const CalendarView = ({ sessions, currentMonth, onMonthChange }) => {
    const monthName = currentMonth.toLocaleString(i18n.language || 'fr', { month: 'long', year: 'numeric' });

    const generateCalendarDays = () => {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDay = new Date(firstDay);
      startDay.setDate(firstDay.getDate() - firstDay.getDay());
      const endDay = new Date(lastDay);
      endDay.setDate(lastDay.getDate() + (6 - lastDay.getDay()));

      const days = [];
      const currentDay = new Date(startDay);
      while (currentDay <= endDay) {
        days.push(new Date(currentDay));
        currentDay.setDate(currentDay.getDate() + 1);
      }
      return days;
    };

    const isToday = (date) => {
      const today = new Date();
      return date.getDate() === today.getDate() &&
             date.getMonth() === today.getMonth() &&
             date.getFullYear() === today.getFullYear();
    };
    const isCurrentMonth = (date) => date.getMonth() === currentMonth.getMonth();
    const changeMonth = (direction) => {
      const newMonth = new Date(currentMonth);
      newMonth.setMonth(newMonth.getMonth() + direction);
      onMonthChange(newMonth);
    };
    const calendarDays = generateCalendarDays();
    const weekdaysShort = t('calendar.weekdaysShort', { returnObjects: true }) || (
      lang === 'en' ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'] :
      lang === 'nl' ? ['Zo','Ma','Di','Wo','Do','Vr','Za'] :
      ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam']
    );

    return (
      <div className="calendar-container">
        <div className="calendar-header">
          <div className="calendar-nav">
            <button onClick={() => changeMonth(-1)}>&lt; {t('calendar.prev')}</button>
            <button onClick={() => changeMonth(1)}>{t('calendar.next')} &gt;</button>
          </div>
          <h2>{monthName}</h2>
        </div>

        <div className="calendar-grid">
          {weekdaysShort.map(day => (
            <div key={day} className="calendar-day-header">{day}</div>
          ))}

          {calendarDays.map((day, index) => {
            const daySessions = (() => {
   // On récupère les sessions du jour
   const raw = sessions.filter(s => s.dateString === toLocalDateString(day));
   // Puis on déduplique: même cours + même heure => une seule entrée
   const map = new Map();
   for (const s of raw) {
     if (!s.startTime) continue; // ignore les enregistrements incomplets
     const key = `${s.courseId}|${s.startTime}`;
     if (!map.has(key)) map.set(key, s);
   }
   // Et on trie par heure de début pour l’affichage
   return Array.from(map.values()).sort((a, b) => a.startTime.localeCompare(b.startTime));
 })();
            return (
              <div 
                key={index} 
                className={`calendar-day ${!isCurrentMonth(day) ? 'other-month' : ''} ${isToday(day) ? 'today' : ''}`}
                onClick={() => openModal(day, daySessions)}
              >
                <div className="day-number">{day.getDate()}</div>
                {daySessions.length > 0 && (
                  <div className="day-sessions">
                    {daySessions.slice(0, 2).map(session => {
                      const title = pickL(session.title_i18n ?? session.title, i18n.language, 'fr');
                      return (
                        <div key={session.id} className={`calendar-session ${session.isNewCourse ? 'calendar-session--new' : ''} ${session.isFull ? 'calendar-session--full' : ''}`}>
                          <div className="session-time">{session.startTime}</div>
                          <div className="session-title">{session.isNewCourse ? '🆕 ' : ''}{session.isFull ? '🔒 ' : ''}{title}</div>
                        </div>
                      );
                    })}
                    {daySessions.length > 2 && (
                      <div className="calendar-session">
                        <div className="session-title">+{daySessions.length - 2}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="loading">{t('loading')}</div>;
  }

  const weekDaysSorted = Object.keys(weeklyView).sort((a, b) => (DAY_ORDER[a] || 8) - (DAY_ORDER[b] || 8));
  const dayLabel = (frDay) => (DAY_LABELS[lang]?.[frDay]) || frDay;

  return (
    <div className="schedule-container">
      <SEO 
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
      />
      <h1 className="page-title">{t('pageTitle')}</h1>

      <section className="schedule-filter-panel" aria-labelledby="schedule-filter-title">
        <div className="schedule-filter-header">
          <div>
            <h2 id="schedule-filter-title" className="schedule-filter-title">
              {lang === 'fr' ? 'Filtrer les cours' : lang === 'nl' ? 'Cursussen filteren' : 'Filter courses'}
            </h2>
            <p className="schedule-filter-subtitle">
              {lang === 'fr'
                ? `${filteredSessions.length} séance${filteredSessions.length > 1 ? 's' : ''} affichée${filteredSessions.length > 1 ? 's' : ''}`
                : lang === 'nl'
                  ? `${filteredSessions.length} les${filteredSessions.length !== 1 ? 'sen' : ''} weergegeven`
                  : `${filteredSessions.length} session${filteredSessions.length !== 1 ? 's' : ''} shown`}
            </p>
          </div>

          {(selectedCourseId || selectedDanceStyle || selectedAudience || selectedDifficulty) && (
            <button type="button" className="reset-filters-link" onClick={resetFilters}>
              {filterLabels.reset}
            </button>
          )}
        </div>

        <div className="schedule-filters">
        <div className="filter-field">
          <label htmlFor="filter-course">{filterLabels.course}</label>
          <select id="filter-course" value={selectedCourseId} onChange={e => setSelectedCourseId(e.target.value)}>
            <option value="">{filterLabels.allCourses}</option>
            {courses.map(course => (
              <option key={course.id} value={course.id}>
                {pickL(course.title_i18n ?? course.title, i18n.language, 'fr')}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-field">
          <label htmlFor="filter-style">{filterLabels.style}</label>
          <select id="filter-style" value={selectedDanceStyle} onChange={e => setSelectedDanceStyle(e.target.value)}>
            <option value="">{filterLabels.allStyles}</option>
            {danceStyleFilterOptions.map((style) => (
              <option key={style.id} value={style.id}>
                {style.parentLabel
                  ? `${style.parentLabel} — ${style.label}`
                  : style.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-field">
          <label htmlFor="filter-audience">{filterLabels.audience}</label>
          <select id="filter-audience" value={selectedAudience} onChange={e => setSelectedAudience(e.target.value)}>
            <option value="">{filterLabels.allAudiences}</option>
            {Object.keys(AUD_KEY).filter(value => value !== 'Tout public').map(value => (
              <option key={value} value={value}>{t(`audiences.${AUD_KEY[value]}`)}</option>
            ))}
          </select>
        </div>

        <div className="filter-field">
          <label htmlFor="filter-level">{filterLabels.level}</label>
          <select id="filter-level" value={selectedDifficulty} onChange={e => setSelectedDifficulty(e.target.value)}>
            <option value="">{filterLabels.allLevels}</option>
            {Object.keys(DIFF_KEY).map(value => (
              <option key={value} value={value}>{t(`difficulties.${DIFF_KEY[value]}`)}</option>
            ))}
          </select>
        </div>

        <button type="button" className="reset-filters-button" onClick={resetFilters}>
          {filterLabels.reset}
        </button>
        </div>
      </section>

      {filteredSessions.length === 0 && (
        <p className="filter-empty-message">{filterLabels.noResults}</p>
      )}


      <section className="weekly-view">
        <h2>{t('weekly.title')}</h2>

        {weekDaysSorted.length === 0 ? (
          <p className="no-courses">{t('weekly.empty')}</p>
        ) : (
          <div className="days-grid">
            {weekDaysSorted.map(day => (
              <div key={day} className="day-column">
                <h3 className="day-header">{dayLabel(day)}</h3>

                {groupByTimeSlot(weeklyView[day]).map(group => (
                  <div
                    key={`${day}-${group.startTime}`}
                    className={`time-slot-row ${group.sessions.length > 1 ? 'time-slot-row--multi' : ''}`}
                  >
                    {group.sessions.map(session => {
                      const title = pickL(session.title_i18n ?? session.title, i18n.language, 'fr');
                      return (
                        <button
                          key={session.id}
                          type="button"
                          className="class-card compact-class-card"
                          onClick={() => openModal(session.date || new Date(), [session])}
                          aria-label={`${title}, ${session.startTime} - ${session.endTime}`}
                          itemScope
                          itemType="http://schema.org/Event"
                        >
                          {session.date && (
                            <meta itemProp="startDate" content={session.date.toISOString()} />
                          )}

                          <span className="compact-card-main">
                            <span className="compact-card-title" itemProp="name">
                              {title}
                              {session.isNewCourse && (
                                <span className="new-badge compact-new-badge">NEW</span>
                              )}
                              {session.isFull && (
                                <span className="full-badge compact-full-badge">🔒</span>
                              )}
                            </span>

                            <span className="compact-card-time">
                              {session.startTime} - {session.endTime}
                            </span>
                          </span>

                          <span className="compact-card-more" aria-hidden="true">›</span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="calendar-section">
        <h2>{t('calendar.title')}</h2>
        <div className="monthly-calendar">
          <CalendarView 
            sessions={filteredSessions} 
            currentMonth={currentMonth} 
            onMonthChange={setCurrentMonth} 
          />
        </div>
      </section>

      <Modal 
        isOpen={isModalOpen} 
        onClose={closeModal} 
        date={selectedDate} 
        sessions={selectedDateSessions} 
      />
    </div>
  );
};

export default Schedule;